import type {
  AxiosError,
  AxiosResponse,
  AxiosResponseTransformer,
  InternalAxiosRequestConfig,
} from 'axios';

import { BaseLanguageType, LOCAL_STORAGE_KEY_LANG } from '@dvb/common';
import { useAuthStore } from '@school-manage/context/useAuthStore';
import { router } from '@school-manage/router';
import { toast } from '@school-manage/utils/toast';
import { onlineManager } from '@tanstack/react-query';
import axios from 'axios';
import { t } from 'i18next';

const responseTransformer: AxiosResponseTransformer = (response: string) => {
  try {
    const parsedRes = JSON.parse(response);
    return parsedRes.data != null ? parsedRes.data : parsedRes;
  } catch (error) {
    return response;
  }
};

const baseRequestInterceptor = (config: InternalAxiosRequestConfig) => {
  const { headers } = config;
  /**
   * This header is a workaround for the backend's CORS policy. Since Safari GET request
   * will also send the preflight request, and Laravel will return empty value for header
   * "Access-Control-Allow-Headers", it will make Safari throw error for the access control checks.
   * So we set an extra header to let Laravel return the non-empty value.
   */
  headers.Lang =
    headers.Lang ||
    localStorage.getItem(LOCAL_STORAGE_KEY_LANG) ||
    BaseLanguageType.EN;
  return config;
};

const authRequestInterceptor = (config: InternalAxiosRequestConfig) => {
  const { headers } = config;
  const { authData } = useAuthStore.getState();
  headers.Authorization = `Bearer ${authData?.token}`;
  return config;
};

const baseResponseInterceptor = (response: AxiosResponse) => response;

const baseErrorInterceptor = (error: AxiosError) => {
  /**
   * Since there may have multiple requests through 401, so we need to check authData exist or not
   */
  if (error.response?.status === 401 && useAuthStore.getState().authData) {
    useAuthStore.getState().removeUser();
    toast.warn(t('sessionExpiredPleaseLoginAgain'));
    router.navigate({
      replace: true,
      search: { from: router.state.location.href },
      to: '/login',
    });
    return Promise.reject(error?.response?.data);
  }

  const isOnline = onlineManager.isOnline();
  if (!isOnline) {
    toast.error(t('currentNoInternet', { ns: 'ui' }));
    return Promise.reject(t('currentNoInternet', { ns: 'ui' }));
  }
  return Promise.reject(error?.response?.data || error?.response || error);
};

const externalAxios = axios.create();

const baseAxios = axios.create();
baseAxios.defaults.baseURL = import.meta.env.VITE_APP_API_BASE_URL;
baseAxios.defaults.transformResponse = responseTransformer;
baseAxios.interceptors.request.use(baseRequestInterceptor);
baseAxios.interceptors.response.use(
  baseResponseInterceptor,
  baseErrorInterceptor
);

const authAxios = axios.create();
authAxios.defaults.baseURL = import.meta.env.VITE_APP_API_BASE_URL;
authAxios.defaults.transformResponse = responseTransformer;
authAxios.interceptors.response.use(
  baseResponseInterceptor,
  baseErrorInterceptor
);

authAxios.interceptors.request.use(baseRequestInterceptor);
authAxios.interceptors.request.use(authRequestInterceptor);

export { authAxios, baseAxios, externalAxios };
